import React, { useState, useRef, useEffect, useCallback } from "react";
import { DEBOUNCE_MS } from "./constants";
import styles from "./OrderInitiatePage.module.css";

/** Keep margin-to-deploy % in [0, 100] while typing (text input). */
export function clampMarginInputValue(value) {
  if (value === "" || value == null) return "";
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  if (num > 100) return "100";
  if (num < 0) return "0";
  return value;
}

function multiplierFromRow(row) {
  const manual = row.multiplier !== undefined && String(row.multiplier).trim() !== "";
  return manual ? String(row.multiplier) : String(row.suggestedMultiplier ?? "1");
}

function ClientRow({ row, updateClientRow, showCheckbox = true }) {
  const [localMargin, setLocalMargin] = useState(row.marginToDeploy);
  const [localMultiplier, setLocalMultiplier] = useState(() => multiplierFromRow(row));
  const marginTimerRef = useRef(null);
  const multiplierTimerRef = useRef(null);
  const marginFieldFocusedRef = useRef(false);
  const multiplierFieldFocusedRef = useRef(false);

  useEffect(() => {
    if (!marginFieldFocusedRef.current) {
      setLocalMargin(row.marginToDeploy);
    }
    if (!multiplierFieldFocusedRef.current) {
      setLocalMultiplier(multiplierFromRow(row));
    }
  }, [row]);

  useEffect(() => {
    return () => {
      if (marginTimerRef.current) clearTimeout(marginTimerRef.current);
      if (multiplierTimerRef.current) clearTimeout(multiplierTimerRef.current);
    };
  }, []);

  const flushMargin = useCallback(
    (value) => {
      let clamped = value;
      const num = parseFloat(value);
      if (value !== "" && !Number.isNaN(num)) {
        if (num > 100) clamped = "100";
        else if (num < 0) clamped = "0";
      }
      updateClientRow(row.id, "marginToDeploy", clamped);
    },
    [row.id, updateClientRow]
  );

  const flushMultiplier = useCallback(
    (value) => updateClientRow(row.id, "multiplier", value),
    [row.id, updateClientRow]
  );

  const handleMarginFocus = () => {
    marginFieldFocusedRef.current = true;
  };

  const handleMarginChange = (e) => {
    const v = e.target.value;
    const clamped = clampMarginInputValue(v);
    setLocalMargin(clamped);
    if (marginTimerRef.current) clearTimeout(marginTimerRef.current);
    marginTimerRef.current = setTimeout(() => flushMargin(clamped), DEBOUNCE_MS);
  };

  const handleMarginBlur = () => {
    if (marginTimerRef.current) {
      clearTimeout(marginTimerRef.current);
      marginTimerRef.current = null;
    }
    const num = parseFloat(localMargin);
    let clamped = localMargin;
    if (localMargin !== "" && !Number.isNaN(num)) {
      if (num > 100) clamped = "100";
      else if (num < 0) clamped = "0";
      setLocalMargin(clamped);
    }
    flushMargin(clamped);
    marginFieldFocusedRef.current = false;
  };

  const handleMultiplierFocus = () => {
    multiplierFieldFocusedRef.current = true;
  };

  const handleMultiplierChange = (e) => {
    const v = e.target.value;
    setLocalMultiplier(v);
    if (multiplierTimerRef.current) clearTimeout(multiplierTimerRef.current);
    multiplierTimerRef.current = setTimeout(() => flushMultiplier(v), DEBOUNCE_MS);
  };

  const handleMultiplierBlur = () => {
    if (multiplierTimerRef.current) {
      clearTimeout(multiplierTimerRef.current);
      multiplierTimerRef.current = null;
    }
    flushMultiplier(localMultiplier);
    multiplierFieldFocusedRef.current = false;
  };

  return (
    <tr>
      {showCheckbox ? (
        <td className={styles.clientTdCheck}>
          <input
            type="checkbox"
            checked={row.checked}
            onChange={(e) => updateClientRow(row.id, "checked", e.target.checked)}
            className={styles.clientCheckbox}
          />
        </td>
      ) : null}
      <td className={styles.clientTd}>{row.clientCode}</td>
      <td className={styles.clientTd}>{row.availableMargin}</td>
      <td className={`${styles.clientTd} ${styles.clientTdRight}`}>
        <input
          type="text"
          value={localMargin}
          onFocus={handleMarginFocus}
          onChange={handleMarginChange}
          onBlur={handleMarginBlur}
          className={styles.clientInput}
          placeholder="—"
        />
      </td>
      <td className={`${styles.clientTd} ${styles.clientTdRight}`}>
        <input
          type="text"
          value={localMultiplier}
          onFocus={handleMultiplierFocus}
          onChange={handleMultiplierChange}
          onBlur={handleMultiplierBlur}
          className={styles.clientInput}
          placeholder="0"
        />
      </td>
      <td
        className={`${styles.clientTd} ${styles.clientTdRight} ${
          row.marginStatus ? styles[`requiredMargin${row.marginStatus}`] : ""
        }`}
      >
        {row.requiredMargin}
      </td>
    </tr>
  );
}

export default React.memo(ClientRow);
